import { getSupabase } from 'src/utils/supabase';

import { requireAuth, withSecurity } from './secureServiceWrapper';

export interface SubscriptionPlan {
  id: string;
  name: string;
  display_name: string;
  price_usd_cents: number;
  price_cad_cents: number;
  trial_days: number;
  is_contact_only: boolean;
  features: Record<string, number>;
  sort_order: number;
}

export interface UserSubscription {
  id: string;
  user_id: string;
  plan_id: string;
  status: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  currency: string;
  trial_start: string | null;
  trial_end: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  created_at: string;
  updated_at: string;
  upgrade_offer_plan_id: string | null;
  upgrade_offer_coupon_id: string | null;
  upgrade_offer_currency: string | null;
  upgrade_offer_approved_at: string | null;
  upgrade_offer_approved_by: string | null;
  upgrade_requested_at: string | null;
  subscription_plans?: SubscriptionPlan;
}

export interface UsageRecord {
  feature_key: string;
  count: number;
  period_start: string;
  period_end: string;
}

export interface SubscriptionDiscount {
  id: string;
  couponId: string;
  name: string;
  percentOff: number | null;
  amountOff: number | null;
  currency: string | null;
  duration: string;
  durationInMonths: number | null;
  end: number | null;
  start: number | null;
}

export const subscriptionService = {
  getPlans: async (): Promise<SubscriptionPlan[]> => {
    const supabase = await getSupabase();
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .eq('is_active', true)
      .order('sort_order');

    if (error) throw new Error('Failed to load plans');
    return data || [];
  },

  getCurrentSubscription: async (): Promise<UserSubscription | null> => {
    return withSecurity(async () => {
      const userId = await requireAuth();
      const supabase = await getSupabase();

      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('*, subscription_plans!plan_id(*)')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) return null;
      return data as UserSubscription | null;
    }, {});
  },

  getUsageStats: async (): Promise<UsageRecord[]> => {
    return withSecurity(async () => {
      const userId = await requireAuth();
      const supabase = await getSupabase();

      const periodStart = new Date();
      periodStart.setDate(1);
      const periodStartStr = periodStart.toISOString().split('T')[0];

      const { data: monthlyData, error } = await supabase
        .from('usage_tracking')
        .select('feature_key, count, period_start, period_end')
        .eq('user_id', userId)
        .eq('period_start', periodStartStr);

      if (error) throw new Error('Failed to load usage data');

      const { data: clientsAllTime } = await supabase
        .from('usage_tracking')
        .select('feature_key, count, period_start, period_end')
        .eq('user_id', userId)
        .eq('feature_key', 'clients');

      const results = monthlyData || [];
      if (clientsAllTime && clientsAllTime.length > 0) {
        const totalClients = clientsAllTime.reduce((sum, r) => sum + r.count, 0);
        const existingIdx = results.findIndex((r) => r.feature_key === 'clients');
        const clientRecord = {
          feature_key: 'clients',
          count: totalClients,
          period_start: periodStartStr,
          period_end: periodStartStr,
        };
        if (existingIdx >= 0) {
          results[existingIdx] = clientRecord;
        } else {
          results.push(clientRecord);
        }
      }

      return results;
    }, {});
  },

  createCheckoutSession: async (planId: string | null, currency: string): Promise<string> => {
    return withSecurity(async () => {
      const supabase = await getSupabase();
      const body: Record<string, string> = { currency };
      if (planId) body.plan_id = planId;
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body,
      });

      if (error || !data?.data?.url) throw new Error('Failed to create checkout session');
      return data.data.url;
    }, {});
  },

  requestUpgrade: async (): Promise<string | null> => {
    return withSecurity(async () => {
      await requireAuth();
      const supabase = await getSupabase();
      const { data, error } = await supabase.rpc('request_upgrade');
      if (error) throw new Error('Failed to send upgrade request');
      return (data as string | null) ?? null;
    }, {});
  },

  createPortalSession: async (): Promise<string> => {
    return withSecurity(async () => {
      const supabase = await getSupabase();
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: {},
      });

      if (error || !data?.data?.url) throw new Error('Failed to create portal session');
      return data.data.url;
    }, {});
  },

  pauseSubscription: async (): Promise<string> => {
    return withSecurity(async () => {
      const supabase = await getSupabase();
      const { data, error } = await supabase.functions.invoke('manage-subscription', {
        body: { action: 'pause' },
      });

      if (error) throw new Error('Failed to pause subscription');
      const raw = data as { success?: boolean; data?: { message: string } };
      return raw.data?.message ?? 'Subscription paused';
    }, {});
  },

  resumeSubscription: async (): Promise<string> => {
    return withSecurity(async () => {
      const supabase = await getSupabase();
      const { data, error } = await supabase.functions.invoke('manage-subscription', {
        body: { action: 'resume' },
      });

      if (error) throw new Error('Failed to resume subscription');
      const raw = data as { success?: boolean; data?: { message: string } };
      return raw.data?.message ?? 'Subscription resumed';
    }, {});
  },

  previewUpgrade: async (
    planId: string,
    currency: string
  ): Promise<{ prorationAmount: number; newPlanPrice: number; currency: string; immediateCharge: number }> => {
    return withSecurity(async () => {
      const supabase = await getSupabase();
      const { data, error } = await supabase.functions.invoke('manage-subscription', {
        body: { action: 'preview_upgrade', planId, currency },
      });

      if (error) throw new Error('Failed to preview upgrade');
      const raw = data as {
        success?: boolean;
        data?: { prorationAmount: number; newPlanPrice: number; currency: string; immediateCharge: number };
      };
      if (!raw.data) throw new Error('Failed to preview upgrade');
      return raw.data;
    }, {});
  },

  getDiscount: async (): Promise<SubscriptionDiscount | null> => {
    return withSecurity(async () => {
      const supabase = await getSupabase();
      const { data, error } = await supabase.functions.invoke('get-subscription-discount', {
        body: {},
      });

      if (error) return null;
      const raw = data as { success?: boolean; data?: { discount: SubscriptionDiscount | null } };
      return raw.data?.discount ?? null;
    }, {});
  },
};
